import React, { useEffect, useState } from 'react';
import { Card, Typography, Button, Spin, Empty } from 'antd';
import { WindowsOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from '../../config/axiosInstance';
import { useTheme } from "../../context/ThemeContext";

const { Title, Text } = Typography;

const extractVersionFromLink = (link) => {
    try {
        const url = new URL(link);
        const pathname = decodeURIComponent(url.pathname);

        let match = pathname.match(/Regasus[-_]?v?(\d+(?:\.\d+)*)(?=[-_\.].*|\.zip)$/i);
        if (match) return match[1];

        match = pathname.match(/Regasus[-_]?([^/]+)\.zip$/i);
        if (match) return match[1];
    } catch (_) {
        // Ignore parsing errors
    }

    return null;
};

const SelfcheckinCard = () => {
    const { darkTheme } = useTheme();
    const [appData, setAppData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAppData = async () => {
            try {
                const response = await axios.get('/apps/selfCheckIn');

                if (response.data?.success) {
                    const data = response.data.data ?? response.data;

                    const derivedVersion =
                        !data.version || data.version === 'unknown'
                            ? extractVersionFromLink(data.link)
                            : data.version;

                    setAppData({
                        ...data,
                        version: derivedVersion ?? data.version ?? 'unknown'
                    });
                } else {
                    setAppData(null);
                }
            } catch (error) {
                console.error('Error fetching SelfCheckIn data:', error);
                setAppData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchAppData();
    }, []);

    const downloadFile = (url) => {
        const a = document.createElement('a');
        a.href = url;
        a.setAttribute('download', '');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const displayVersion =
        appData?.version && appData.version !== 'unknown'
            ? appData.version
            : extractVersionFromLink(appData?.link) ?? '—';

    return (
        <Card
            style={{
                backgroundColor: darkTheme ? '#141414' : '#ffffff',
                border: darkTheme ? '1px solid #303030' : '1px solid #f0f0f0'
            }}
        >
            <Title level={4} style={{ marginTop: 0, marginBottom: 20 }}>
                Self Checkin
            </Title>

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
                    <Spin />
                </div>
            ) : !appData ? (
                <Empty description="No data available" />
            ) : (
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <Card
                        size="small"
                        style={{
                            width: 180,
                            textAlign: 'center',
                            backgroundColor: darkTheme ? '#1f1f1f' : '#ffffff',
                            border: darkTheme ? '1px solid #303030' : '1px solid #f0f0f0',
                            borderRadius: '10px',
                            boxShadow: darkTheme
                                ? '0 2px 8px rgba(255, 255, 255, 0.08)'
                                : '0 2px 8px rgba(0, 0, 0, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            minHeight: 200
                        }}
                    >
                        <WindowsOutlined
                            style={{
                                fontSize: 42,
                                color: darkTheme ? '#00adef' : '#0078D6'
                            }}
                        />

                        <Title level={5}>Windows</Title>
                        <Text>Version: {displayVersion}</Text>

                        <div style={{ marginTop: 16 }}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={() => downloadFile(appData.link)}
                                disabled={!appData.link}
                            >
                                Download
                            </Button>
                        </div>
                    </Card>
                </div>
            )}
        </Card>
    );
};

export default SelfcheckinCard;